import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

// PWA PIN window re-check only matters when the installable PWA is enabled.
const PWA_ENABLED = process.env.REACT_APP_PWA_ENABLED === "true";

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [csrf, setCsrf] = useState(null);
  const [loading, setLoading] = useState(true);
  // Paired mobile session exists but the Rotary Access PIN has not been verified yet.
  const [pinLocked, setPinLocked] = useState(false);
  const didInitialLoad = useRef(false);

  // silent = background re-verification: keeps the current screen visible (never
  // flips to the full-screen loader) and only updates state on a real change.
  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const r = await api.get("/auth/me");
      setUser(r.data);
      setCsrf(r.data.csrf_token);
      setPinLocked(false);
    } catch (err) {
      // On a silent check, a transient network blip must NOT sign the user out.
      const status = err?.response?.status;
      if (silent && status !== 401) {
        // keep existing session; ignore
      } else {
        setUser(null);
        setCsrf(null);
        setPinLocked(status === 401 && err?.response?.data?.detail === "pin_unlock_required");
      }
    }
    if (!silent) setLoading(false);
    didInitialLoad.current = true;
  }, []);

  // Verify once on initial app load only.
  useEffect(() => {
    load();
  }, [load]);

  // Returning to a backgrounded PWA re-checks the server-side PIN window (>5 min => locked).
  // Responsive web keeps a persistent HTTP-only session cookie, so we do NOT re-verify on
  // every tab switch/focus — that caused a multi-second re-check lag on resume.
  useEffect(() => {
    if (!PWA_ENABLED) return;
    const onVisible = () => {
      if (document.visibilityState === "visible" && didInitialLoad.current) load(true);
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [load]);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout", {}, { headers: { "X-CSRF-Token": csrf } });
    } catch (_) {}
    setUser(null);
    setCsrf(null);
    window.location.href = "/login";
  }, [csrf]);

  return (
    <AuthContext.Provider value={{ user, csrf, loading, pinLocked, refresh: load, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
